/**
 * 大会本部のパイバル（風）のシート（docs/08 §198）。
 *
 * 地図右のボタン（タスクシートの下）で開く、下からのシート。地図を見ながら
 * 円形チャートと高度ごとの表を見られる。
 * - まとまり（日付＋午前・午後）を選び、その中の回（約20分おき）を切り替える。
 *   午前と午後の風は混ぜない
 * - 円形チャートは「上昇しながら流される道すじ」（公式ウェザーシートの
 *   WIND CHARTと同じ考え方、上昇率は選べる）。選んだ回を濃く、同じまとまりの
 *   前の回を薄く重ねる
 * - 高度はオクレンジャーの数字（CLPからの対地[m]）のまま。CLPの標高が
 *   分かれば海抜[ft]も並べる（CLPは選び直せる）
 * - 新しい回が届いたら、右のボタンに印を付ける
 */

import { KNOT_MPS, pibalSessionLabel, type OfficialPibal } from '@gv4d/core';

import { sessionsOf, type FeedPibal, type OfficialPibalFeed, type PibalFeedState } from './data/official-pibal-feed.ts';
import { latestOwnTeamOut, setupMarkerPane } from './marker-panel.ts';
import { onDrag, SHEET_STOPS, snapSheetRatio } from './taskboard-panel.ts';
import {
  CLIMB_RATES_MPM,
  DEFAULT_CLIMB_RATE_MPM,
  downwindBearing,
  formatDistance,
  pibalChartSvg,
  pibalTrajectory,
  type PibalSpeedUnit,
} from './ui/pibal-chart.ts';

export interface ClpCandidate {
  name: string;
  lat: number;
  lon: number;
  /** 会場のゴールデータに入っている標高 [m]（無ければ null） */
  elevM: number | null;
}

export interface PibalPanelOptions {
  feed: OfficialPibalFeed;
  /** 今の会場（CLPの選択を会場ごとに覚える） */
  venueId: () => string;
  /** CLPの候補（会場のゴールのうち名前がCLPのもの。無ければ空） */
  clpCandidates: () => ClpCandidate[];
  /** その地点の標高 [m]（地形データが読めていれば）。分からなければ null */
  elevationAt: (lon: number, lat: number) => number | null;
  /** 海抜[m] → 色（地図の航跡と同じ高度の色分け） */
  colorForMsl: (altMslM: number) => string;
  /** 開く前に合言葉を確かめる（未認証なら入力を促す） */
  ensureAuth: () => Promise<boolean>;
  /**
   * 計測のターゲットの座標（例 "1806/0288"）を地図に出す。`coverBottomPx`は
   * シートが画面の下を覆っている高さ（地図の中心をシートの上に寄せるのに使う）
   */
  onShowTarget: (coordinates: string, label: string, coverBottomPx: number) => void;
}

interface PanelPrefs {
  /** 流される道すじを描くときの上昇率 [m/分] */
  climbRateMpm: number;
  unit: PibalSpeedUnit;
  sheetRatio: number;
  /** 開いているタブ */
  tab: 'wind' | 'markers';
}

const PREFS_KEY = 'gv4d-official-pibal-prefs';
const CLP_KEY_PREFIX = 'gv4d-official-pibal-clp:';

function loadPrefs(): PanelPrefs {
  const base: PanelPrefs = { climbRateMpm: DEFAULT_CLIMB_RATE_MPM, unit: 'kt', sheetRatio: 0.55, tab: 'wind' };
  try {
    const raw = localStorage.getItem(PREFS_KEY);
    return raw ? { ...base, ...(JSON.parse(raw) as Partial<PanelPrefs>) } : base;
  } catch {
    return base;
  }
}

function savePrefs(p: PanelPrefs): void {
  try {
    localStorage.setItem(PREFS_KEY, JSON.stringify(p));
  } catch {
    // 保存できなくても今回は効く
  }
}

interface ClpChoice {
  /** 候補の名前。'' は「手入力」 */
  name: string;
  /** 手入力の標高 [m]（候補を選んだときも、地形から分からなければこれを使う） */
  elevM: number | null;
}

function loadClp(venueId: string): ClpChoice | null {
  try {
    const raw = localStorage.getItem(CLP_KEY_PREFIX + venueId);
    return raw ? (JSON.parse(raw) as ClpChoice) : null;
  } catch {
    return null;
  }
}

function saveClp(venueId: string, c: ClpChoice): void {
  try {
    localStorage.setItem(CLP_KEY_PREFIX + venueId, JSON.stringify(c));
  } catch {
    // 保存できなくても今回は効く
  }
}

const hhmm = (local: string | null) => (local ? local.slice(11, 16) : '--:--');
const fmt1 = (n: number) => (Math.round(n * 10) / 10).toFixed(1);

/** 角度の差（-180〜180） */
export function angleDiff(a: number, b: number): number {
  return ((a - b + 540) % 360) - 180;
}

/** 前の回と比べた変化（同じ高度どうし）。前の回にその高度が無ければ null */
export function levelChange(
  current: OfficialPibal['levels'][number],
  previous: OfficialPibal | undefined,
): { dDir: number | null; dSpeedKt: number } | null {
  const p = previous?.levels.find((l) => Math.abs(l.altAglM - current.altAglM) < 1);
  if (!p) return null;
  const dDir = current.dirFromDeg !== null && p.dirFromDeg !== null ? angleDiff(current.dirFromDeg, p.dirFromDeg) : null;
  return { dDir, dSpeedKt: current.speedKt - p.speedKt };
}

function formatTime(ms: number | null): string {
  if (!ms) return '--:--';
  const d = new Date(ms);
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

/** 取り込みの状況の一行（「最終取得 15:41・2分おき（6:00〜8:00、14:30〜16:30）」など） */
/** 方位を3桁の度で（005°・172°）。360° は 000° に */
export function degText(deg: number): string {
  return `${String(Math.round(deg) % 360).padStart(3, '0')}°`;
}

/** 受信プログラム（WhatsApp等）の状態の一言。知らせが3分以上来なければ「止まっている」 */
export function receiverLine(
  receiver: NonNullable<PibalFeedState['status']['receiver']> | null | undefined,
  now = Date.now(),
): { text: string; warn: boolean } | null {
  if (!receiver) return null;
  const name = receiver.source === 'whatsapp' ? 'WhatsApp受信' : `${receiver.source}受信`;
  if (now - receiver.reportedAt > 3 * 60000) {
    return { text: `${name}: 止まっています（最後の連絡 ${formatTime(receiver.reportedAt)}）`, warn: true };
  }
  const pending = receiver.pending > 0 ? `・未送信 ${receiver.pending}件` : '';
  if (!receiver.connected) return { text: `${name}: 切断中${receiver.error ? `（${receiver.error}）` : ''}${pending}`, warn: true };
  const last = receiver.lastMessageAt ? `最終受信 ${formatTime(receiver.lastMessageAt)}` : 'まだ受信なし';
  return { text: `${name}: 接続中（${last}）${pending}`, warn: receiver.pending > 0 };
}

/** 取り込みの状況の一行（「最終取得 15:41・2分おき（6:00〜8:00、14:30〜16:30）」など） */
export function statusLine(state: PibalFeedState | null, fetchError: string | null, now = Date.now()): { text: string; warn: boolean } {
  if (fetchError) return { text: fetchError, warn: true };
  if (!state) return { text: 'まだ受け取っていません', warn: false };
  const recv = receiverLine(state.status.receiver, now);
  const c = state.config;
  let ocr: { text: string; warn: boolean } | null;
  if (!c.configured) {
    // WhatsAppで受けている大会なら、オクレンジャーが未登録なのは普通
    ocr = recv ? null : { text: 'ブリッジにオクレンジャーのリンクが未登録です（下の「取り込みの設定」。WhatsApp等は貼り付けで送れます）', warn: true };
  } else {
    const windows = c.windows.map((w) => `${w.start.replace(/^0/, '')}〜${w.end.replace(/^0/, '')}`).join('、');
    const plan = c.enabled ? `${c.intervalMin}分おき（${windows || '時間帯なし'}）` : '自動取得は停止中';
    if (state.status.running) ocr = { text: `取得中…・${plan}`, warn: false };
    else if (state.status.lastError) ocr = { text: `取得に失敗（${formatTime(state.status.lastAttemptAt)}）: ${state.status.lastError}`, warn: true };
    else ocr = { text: `最終取得 ${formatTime(state.status.lastSuccessAt)}・${plan}`, warn: false };
  }
  const parts = [recv, ocr].filter((x): x is { text: string; warn: boolean } => x !== null);
  return { text: parts.map((p) => p.text).join(' ／ '), warn: parts.some((p) => p.warn) };
}

export function setupPibalPanel(options: PibalPanelOptions): {
  toggle: () => void;
  refreshBadge: () => void;
  receiveShared: (text: string) => Promise<void>;
} {
  const { feed } = options;
  const $ = <T extends HTMLElement>(id: string) => document.getElementById(id) as T;
  const host = $<HTMLDivElement>('pb-host');
  const head = $<HTMLDivElement>('pb-head');
  const sourceSelect = $<HTMLSelectElement>('pb-source');
  const archiveLine = $<HTMLParagraphElement>('pb-archive-line');
  const archiveText = $<HTMLSpanElement>('pb-archive-text');
  const archiveCsv = $<HTMLAnchorElement>('pb-archive-csv');
  const sessionSelect = $<HTMLSelectElement>('pb-session');
  const releases = $<HTMLDivElement>('pb-releases');
  const status = $<HTMLParagraphElement>('pb-status');
  const chart = $<HTMLDivElement>('pb-chart');
  const meta = $<HTMLParagraphElement>('pb-meta');
  const drift = $<HTMLParagraphElement>('pb-drift');
  const table = $<HTMLTableElement>('pb-table');
  const empty = $<HTMLParagraphElement>('pb-empty');
  const climbSelect = $<HTMLSelectElement>('pb-climb');
  const unitBtn = $<HTMLButtonElement>('pb-unit');
  const clpSelect = $<HTMLSelectElement>('pb-clp');
  const clpElev = $<HTMLInputElement>('pb-clp-elev');
  const clpHint = $<HTMLSpanElement>('pb-clp-hint');

  const prefs = loadPrefs();
  let session: string | null = null;
  let currentId: string | null = null;
  /** 見ている大会: 'live'（今の大会）か、アーカイブのID（docs/08 §212） */
  let source = 'live';
  let archived: { id: string; soundings: OfficialPibal[] | null; error: string | null } | null = null;
  let highlightAlt: number | null = null;

  // --- CLP（高度の基準） -------------------------------------------------------
  /**
   * CLPの標高の決め方（docs/08 §198）: 手入力 → 会場のゴールデータに入っている
   * 標高（一関のCLPは21m）→ 地形データ（3D表示の起伏を使っているときだけ）
   */
  const clp = (): { label: string; elevM: number | null; source: 'manual' | 'goals' | 'terrain' | null } => {
    const saved = loadClp(options.venueId());
    const cands = options.clpCandidates();
    const chosen = cands.find((c) => c.name === (saved ? saved.name : cands[0]?.name));
    const label = chosen?.name ?? '手入力';
    if (saved?.elevM != null) return { label, elevM: saved.elevM, source: 'manual' };
    if (chosen?.elevM != null) return { label, elevM: chosen.elevM, source: 'goals' };
    const terrain = chosen ? options.elevationAt(chosen.lon, chosen.lat) : null;
    return { label, elevM: terrain, source: terrain !== null ? 'terrain' : null };
  };

  const renderClp = () => {
    const cands = options.clpCandidates();
    const saved = loadClp(options.venueId());
    const selected = saved ? saved.name : cands[0]?.name ?? '';
    clpSelect.replaceChildren(
      ...cands.map((c) => new Option(c.name, c.name, false, c.name === selected)),
      new Option('手入力（標高だけ入れる）', '', false, selected === ''),
    );
    const c = clp();
    clpElev.value = saved?.elevM != null ? String(saved.elevM) : '';
    clpElev.placeholder = c.source !== 'manual' && c.elevM !== null ? String(Math.round(c.elevM)) : '標高 m';
    clpHint.textContent =
      c.elevM === null
        ? '標高が分からないため、海抜の高さ（ft）は出せません。3D表示で地形を使うか、標高を入れてください'
        : `${c.label}の標高 ${Math.round(c.elevM)}m（${{ manual: '手入力', goals: '会場のゴールデータ', terrain: '地形データ' }[c.source!]}）。表の「海抜ft」はこれを足した値。違っていれば標高を入れ直してください`;
  };
  const storeClp = () => {
    const v = clpElev.value.trim();
    const n = v === '' ? null : Number(v);
    saveClp(options.venueId(), { name: clpSelect.value, elevM: n !== null && Number.isFinite(n) ? n : null });
    renderClp();
    render();
  };
  clpSelect.onchange = () => {
    clpElev.value = '';
    storeClp();
  };
  clpElev.onchange = storeClp;

  // --- 描画 -----------------------------------------------------------------
  const speedText = (kt: number) => (prefs.unit === 'kt' ? fmt1(kt) : fmt1(kt * KNOT_MPS));

  const renderStatus = () => {
    // 過去の大会を見ている間は、今の取り込みの状況は出さない（紛らわしいので）
    status.hidden = source !== 'live';
    const line = statusLine(feed.state, feed.lastFetchError);
    status.textContent = line.text;
    status.classList.toggle('warn', line.warn);
  };
  // 受信プログラムが止まったこと（知らせが来なくなったこと）は、ブリッジの中身が
  // 変わらないので知らせが届かない。開いている間は30秒おきに状況の一行だけ書き直す
  setInterval(() => {
    if (!host.hidden && prefs.tab === 'wind') renderStatus();
  }, 30000);

  /** 大会の選択欄（アーカイブが1つも無ければ出さない） */
  const renderSource = () => {
    const archives = feed.archives;
    if (source !== 'live' && !archives.some((a) => a.id === source)) {
      source = 'live';
      archived = null;
    }
    sourceSelect.hidden = archives.length === 0;
    const liveName = feed.state?.config.archiveName;
    sourceSelect.replaceChildren(
      new Option(liveName ? `今の大会（${liveName}）` : '今の大会', 'live', false, source === 'live'),
      ...archives.map((a) => new Option(`${a.name}（${a.count}回）`, a.id, false, a.id === source)),
    );
    const a = archives.find((x) => x.id === source);
    archiveLine.hidden = !a;
    if (a) {
      const span = a.firstLocal && a.lastLocal ? `${a.firstLocal.slice(5, 10).replace('-', '/')}〜${a.lastLocal.slice(5, 10).replace('-', '/')}` : '';
      archiveText.textContent = `過去の大会「${a.name}」を表示中（${span}${span ? '・' : ''}${a.count}回）`;
      archiveCsv.href = feed.archiveCsvUrl(a.id);
      archiveCsv.download = `pibal-${a.name}.csv`;
    }
  };
  sourceSelect.onchange = () => {
    source = sourceSelect.value;
    session = null;
    currentId = null;
    archived = source === 'live' ? null : { id: source, soundings: null, error: null };
    render();
    if (archived) {
      const want = archived;
      feed
        .loadArchive(want.id)
        .then((list) => {
          want.soundings = list;
        })
        .catch((e: unknown) => {
          want.error = (e as Error).message;
        })
        .finally(() => {
          if (archived === want) render();
        });
    }
  };

  const render = () => {
    renderSource();
    const list = source === 'live' ? feed.pibals : (archived?.soundings ?? []);
    const sessions = sessionsOf(list);
    renderStatus();
    unitBtn.textContent = prefs.unit === 'kt' ? '速さ: kt' : '速さ: m/s';
    empty.textContent =
      source === 'live'
        ? 'まだパイバルの配信がありません。'
        : archived?.error
          ? `アーカイブを読めませんでした: ${archived.error}`
          : archived?.soundings
            ? 'このアーカイブには風がありません。'
            : 'アーカイブを読み込み中…';

    if (sessions.length === 0) {
      sessionSelect.replaceChildren();
      releases.replaceChildren();
      chart.innerHTML = '';
      table.innerHTML = '';
      meta.textContent = '';
      drift.textContent = '';
      empty.hidden = false;
      return;
    }
    empty.hidden = true;
    if (!session || !sessions.includes(session)) session = sessions[0];
    sessionSelect.replaceChildren(...sessions.map((s) => new Option(pibalSessionLabel(s), s, false, s === session)));

    const inSession = list.filter((p) => p.session === session);
    if (!currentId || !inSession.some((p) => p.id === currentId)) currentId = inSession[0].id;
    const idx = inSession.findIndex((p) => p.id === currentId);
    const current = inSession[idx];
    const previous = inSession.slice(idx + 1);

    releases.replaceChildren(
      ...inSession.map((p) => {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = `pb-chip${p.id === currentId ? ' active' : ''}${(p as FeedPibal).local ? ' local' : ''}`;
        b.textContent = hhmm(p.releasedLocal ?? p.sentLocal);
        b.title = `${p.title || '貼り付け'}（配信 ${hhmm(p.sentLocal)}）`;
        b.onclick = () => {
          currentId = p.id;
          render();
        };
        return b;
      }),
    );

    const base = clp();
    const colorFor = (agl: number) => options.colorForMsl((base.elevM ?? 0) + agl);
    chart.innerHTML = pibalChartSvg({ current, previous, climbRateMpm: prefs.climbRateMpm, colorFor, highlightAltM: highlightAlt });
    // いちばん上まで上がったときに、どちらへどれだけ流されるか
    const end = pibalTrajectory(current, prefs.climbRateMpm).at(-1)!;
    const dist = Math.hypot(end.eastM, end.northM);
    const bearing = Math.round(((Math.atan2(end.eastM, end.northM) * 180) / Math.PI + 360) % 360);
    drift.textContent =
      end.altAglM > 0
        ? `${end.altAglM}mまで上昇（約${Math.round(end.tSec / 60)}分）で ${String(bearing).padStart(3, '0')}°方向へ ${formatDistance(dist)}`
        : '';

    const bits = [
      `放球 ${hhmm(current.releasedLocal)}`,
      `配信 ${hhmm(current.sentLocal)}`,
      current.surfaceTempC !== null ? `地表 ${current.surfaceTempC}℃` : '',
      current.inCloud ? 'in cloud（雲で見失い）' : '',
      (current as FeedPibal).local ? '貼り付けたもの' : '',
      ...current.notes,
    ].filter(Boolean);
    meta.textContent = bits.join('・');

    const prev = previous[0];
    const unitLabel = prefs.unit === 'kt' ? 'kt' : 'm/s';
    const rows = [...current.levels].reverse().map((l) => {
      const ft = base.elevM !== null ? Math.round((base.elevM + l.altAglM) / 0.3048) : null;
      const ch = levelChange(l, prev);
      const arrowDeg = l.dirFromDeg === null ? null : downwindBearing(l.dirFromDeg);
      const change = ch
        ? `${ch.dDir === null ? '' : `${ch.dDir > 0 ? '+' : ''}${Math.round(ch.dDir)}° `}${ch.dSpeedKt >= 0 ? '+' : '−'}${speedText(Math.abs(ch.dSpeedKt))}`
        : '';
      const color = colorFor(l.altAglM);
      return `<tr data-alt="${l.altAglM}" class="${highlightAlt !== null && Math.abs(highlightAlt - l.altAglM) < 1 ? 'hi' : ''}">
        <td><span class="pb-swatch" style="background:${color}"></span>${l.altAglM}</td>
        <td>${ft ?? '—'}</td>
        <td>${l.dirFromDeg === null ? 'calm' : degText(l.dirFromDeg)}</td>
        <td class="pb-to">${
          arrowDeg === null ? '—' : `${degText(arrowDeg)}<span class="pb-arrow" style="transform:rotate(${arrowDeg}deg)">↑</span>`
        }</td>
        <td>${speedText(l.speedKt)}</td>
        <td class="pb-change">${change}</td>
      </tr>`;
    });
    table.innerHTML = `<thead><tr><th>高度m<br><small>CLPから</small></th><th>海抜<br><small>ft</small></th><th>風向 from<br><small>吹いてくる方（真北）</small></th><th>to<br><small>流される方</small></th><th>${unitLabel}</th><th>前回比<br><small>${prev ? hhmm(prev.releasedLocal ?? prev.sentLocal) : ''}</small></th></tr></thead><tbody>${rows.join('')}</tbody>`;
  };

  sessionSelect.onchange = () => {
    session = sessionSelect.value;
    currentId = null;
    render();
  };
  climbSelect.replaceChildren(
    ...CLIMB_RATES_MPM.map((r) => new Option(`上昇 ${r}m/分（${Math.round(r / 0.3048 / 10) * 10}ft/分）`, String(r))),
  );
  if (!(CLIMB_RATES_MPM as readonly number[]).includes(prefs.climbRateMpm)) prefs.climbRateMpm = DEFAULT_CLIMB_RATE_MPM;
  climbSelect.value = String(prefs.climbRateMpm);
  climbSelect.onchange = () => {
    prefs.climbRateMpm = Number(climbSelect.value);
    savePrefs(prefs);
    render();
  };
  unitBtn.onclick = () => {
    prefs.unit = prefs.unit === 'kt' ? 'mps' : 'kt';
    savePrefs(prefs);
    render();
  };
  // 表の行・チャートの点をタップすると、その高度を強調する（もう一度で解除）
  const toggleHighlight = (alt: number) => {
    highlightAlt = highlightAlt !== null && Math.abs(highlightAlt - alt) < 1 ? null : alt;
    render();
  };
  table.addEventListener('click', (e) => {
    const tr = (e.target as HTMLElement).closest('tr[data-alt]');
    if (tr) toggleHighlight(Number(tr.getAttribute('data-alt')));
  });
  chart.addEventListener('click', (e) => {
    const dot = (e.target as Element).closest('[data-alt]');
    if (dot) toggleHighlight(Number(dot.getAttribute('data-alt')));
  });

  // --- 今すぐ取得 ---------------------------------------------------------------
  const fetchBtn = $<HTMLButtonElement>('pb-fetch');
  fetchBtn.onclick = async () => {
    fetchBtn.disabled = true;
    try {
      await feed.fetchNow();
      session = null;
      currentId = null;
    } catch (e) {
      feed.lastFetchError = `今すぐ取得できませんでした: ${(e as Error).message}`;
    } finally {
      fetchBtn.disabled = false;
      render();
    }
  };

  // --- 貼り付け（予備） ----------------------------------------------------------
  const paste = $<HTMLTextAreaElement>('pb-paste');
  const pasteMsg = $<HTMLSpanElement>('pb-paste-msg');
  $<HTMLButtonElement>('pb-paste-add').onclick = () => {
    const added = feed.addPasted(paste.value);
    if (!added) {
      pasteMsg.textContent = 'パイバルの高度の行（例: 100m 352° 5.4kt）も、計測のお知らせ（Task … Target と #番号）も見つかりませんでした';
      return;
    }
    paste.value = '';
    if (added.kind === 'marker') {
      pasteMsg.textContent = `Task ${added.notice.taskNo} の計測のお知らせ（${added.notice.markers.length}件）を追加しました（この端末だけ）`;
      showTab('markers');
      return;
    }
    pasteMsg.textContent = `${added.pibal.levels.length}高度を追加しました（この端末だけ）`;
    session = added.pibal.session;
    currentId = added.pibal.id;
    showTab('wind');
  };

  /** 本文をブリッジへ送り、全員に配る（貼り付けの「全員に送る」と、共有メニューからの起動） */
  const sendToAll = async (text: string, source: string) => {
    pasteMsg.textContent = '送信中…';
    try {
      const message = await feed.submit(text, source);
      pasteMsg.textContent = message;
      paste.value = '';
      session = null;
      currentId = null;
      showTab(/計測/.test(message) ? 'markers' : 'wind');
    } catch (e) {
      pasteMsg.textContent = `送れませんでした: ${(e as Error).message}`;
    }
  };
  $<HTMLButtonElement>('pb-paste-send').onclick = () => {
    if (!paste.value.trim()) {
      pasteMsg.textContent = '本文を貼り付けてください';
      return;
    }
    void sendToAll(paste.value, 'app');
  };

  // --- タブ（風・計測） ----------------------------------------------------------
  const markers = setupMarkerPane({
    feed,
    venueId: options.venueId,
    onShowTarget: (coordinates, label) => {
      // ほぼ全部まで上げたシートだと地図が見えないので、半分まで下げる（タスクシートと同じ）
      if (prefs.sheetRatio > SHEET_STOPS[1]) {
        prefs.sheetRatio = SHEET_STOPS[1];
        savePrefs(prefs);
        applyHeight();
      }
      options.onShowTarget(coordinates, label, Math.round(prefs.sheetRatio * window.innerHeight));
    },
  });
  const tabWind = $<HTMLButtonElement>('pb-tab-wind');
  const tabMarkers = $<HTMLButtonElement>('pb-tab-markers');
  const showTab = (tab: PanelPrefs['tab']) => {
    prefs.tab = tab;
    savePrefs(prefs);
    tabWind.classList.toggle('active', tab === 'wind');
    tabMarkers.classList.toggle('active', tab === 'markers');
    tabWind.setAttribute('aria-selected', String(tab === 'wind'));
    tabMarkers.setAttribute('aria-selected', String(tab === 'markers'));
    $('pb-pane-wind').hidden = tab !== 'wind';
    $('pb-pane-markers').hidden = tab !== 'markers';
    for (const el of host.querySelectorAll<HTMLElement>('.pb-wind-only')) el.hidden = tab !== 'wind';
    if (tab === 'wind') render();
    else markers.render();
  };
  tabWind.onclick = () => showTab('wind');
  tabMarkers.onclick = () => showTab('markers');

  // --- 取り込みの設定（管理者） ---------------------------------------------------
  const cfgNow = $<HTMLParagraphElement>('pb-cfg-now');
  const cfgLink = $<HTMLInputElement>('pb-cfg-link');
  const cfgW1s = $<HTMLInputElement>('pb-cfg-w1s');
  const cfgW1e = $<HTMLInputElement>('pb-cfg-w1e');
  const cfgW2s = $<HTMLInputElement>('pb-cfg-w2s');
  const cfgW2e = $<HTMLInputElement>('pb-cfg-w2e');
  const cfgInterval = $<HTMLInputElement>('pb-cfg-interval');
  const cfgM1s = $<HTMLInputElement>('pb-cfg-m1s');
  const cfgM1e = $<HTMLInputElement>('pb-cfg-m1e');
  const cfgM2s = $<HTMLInputElement>('pb-cfg-m2s');
  const cfgM2e = $<HTMLInputElement>('pb-cfg-m2e');
  const cfgMInterval = $<HTMLInputElement>('pb-cfg-minterval');
  const cfgCompetitors = $<HTMLInputElement>('pb-cfg-competitors');
  const cfgArchiveName = $<HTMLInputElement>('pb-cfg-archive-name');
  const cfgArchivesRow = $<HTMLDivElement>('pb-cfg-archives-row');
  const cfgArchivePick = $<HTMLSelectElement>('pb-cfg-archive-pick');
  const cfgEnabled = $<HTMLInputElement>('pb-cfg-enabled');
  const cfgPassword = $<HTMLInputElement>('pb-cfg-password');
  const cfgMsg = $<HTMLSpanElement>('pb-cfg-msg');
  const renderConfig = () => {
    const c = feed.state?.config;
    cfgNow.textContent = c
      ? c.configured
        ? `リンク登録済み（${c.linkHint}）・${c.enabled ? '自動取得 有効' : '自動取得 停止中'}`
        : 'リンク未登録'
      : 'ブリッジの設定をまだ受け取っていません';
    if (!c) return;
    const [w1, w2] = c.windows;
    // 入力中の欄は書き換えない
    if (document.activeElement?.closest('#pb-config') == null) {
      cfgW1s.value = w1?.start ?? '';
      cfgW1e.value = w1?.end ?? '';
      cfgW2s.value = w2?.start ?? '';
      cfgW2e.value = w2?.end ?? '';
      cfgInterval.value = String(c.intervalMin);
      cfgEnabled.checked = c.enabled;
      const [m1, m2] = c.markerWindows ?? [];
      cfgM1s.value = m1?.start ?? '';
      cfgM1e.value = m1?.end ?? '';
      cfgM2s.value = m2?.start ?? '';
      cfgM2e.value = m2?.end ?? '';
      cfgMInterval.value = c.markerIntervalMin ? String(c.markerIntervalMin) : '';
      cfgCompetitors.placeholder = c.competitorListUrl ?? 'http://www.exem-balloon.com/…/CompetitorList.html';
      cfgArchiveName.placeholder = c.archiveName ?? '未設定（例: 2026佐賀）';
      const archives = feed.archives;
      cfgArchivesRow.hidden = archives.length === 0;
      cfgArchivePick.replaceChildren(...archives.map((a) => new Option(`${a.name}（${a.count}回）`, a.id)));
    }
  };
  /** 送って、うまくいったら true */
  const submitConfig = async (patch: Record<string, unknown>, done: string): Promise<boolean> => {
    if (!cfgPassword.value) {
      cfgMsg.textContent = '管理者用パスワードを入れてください';
      return false;
    }
    cfgMsg.textContent = '送信中…';
    try {
      await feed.saveConfig(cfgPassword.value, patch);
      cfgMsg.textContent = done;
      cfgLink.value = '';
      cfgCompetitors.value = '';
      cfgArchiveName.value = '';
      return true;
    } catch (e) {
      cfgMsg.textContent = `保存できませんでした: ${(e as Error).message}`;
      return false;
    }
  };
  $<HTMLButtonElement>('pb-cfg-save').onclick = () => {
    const pairs = (list: Array<[string, string]>) => list.filter(([s, e]) => s && e).map(([start, end]) => ({ start, end }));
    const windows = pairs([
      [cfgW1s.value, cfgW1e.value],
      [cfgW2s.value, cfgW2e.value],
    ]);
    const patch: Record<string, unknown> = { windows, intervalMin: Number(cfgInterval.value), enabled: cfgEnabled.checked };
    // 古いブリッジ（計測の時間帯を知らない）に送ってもよいよう、入っているときだけ
    if (cfgMInterval.value) {
      patch.markerWindows = pairs([
        [cfgM1s.value, cfgM1e.value],
        [cfgM2s.value, cfgM2e.value],
      ]);
      patch.markerIntervalMin = Number(cfgMInterval.value);
    }
    if (cfgCompetitors.value.trim()) patch.competitorListUrl = cfgCompetitors.value.trim();
    if (cfgArchiveName.value.trim()) patch.archiveName = cfgArchiveName.value.trim();
    if (cfgLink.value.trim()) patch.link = cfgLink.value.trim();
    void submitConfig(patch, patch.link ? '保存しました。リンクを確かめるため、すぐ1回読みに行きます' : '保存しました');
  };
  $<HTMLButtonElement>('pb-cfg-archive').onclick = async () => {
    const name = cfgArchiveName.value.trim() || feed.state?.config.archiveName || '';
    if (!name) {
      cfgMsg.textContent = '大会名（アーカイブの名前）を入れてください';
      cfgArchiveName.focus();
      return;
    }
    const count = feed.state?.soundings.length ?? 0;
    if (
      !confirm(
        `今の大会の風（${count}回）を「${name}」としてアーカイブに残し、風・計測のお知らせ・競技者一覧を全員の画面から消しますか？\n` +
          '残した風は、風のシートの選択欄で過去の大会として見られます。リンクや時間帯の設定は残ります。',
      )
    ) {
      return;
    }
    if (await submitConfig({ archiveAs: name }, `「${name}」に残して、今の大会の情報を消しました（この端末で貼り付けたものも消しました）`)) {
      feed.clearLocal();
    }
  };
  $<HTMLButtonElement>('pb-cfg-archive-del').onclick = async () => {
    const a = feed.archives.find((x) => x.id === cfgArchivePick.value);
    if (!a || !confirm(`アーカイブ「${a.name}」（風${a.count}回）を消しますか？元には戻せません。`)) return;
    await submitConfig({ deleteArchive: a.id }, `アーカイブ「${a.name}」を消しました`);
  };
  $<HTMLButtonElement>('pb-cfg-wipe').onclick = async () => {
    if (
      !confirm(
        '取り込んだ情報（風・計測のお知らせ・競技者一覧）を、アーカイブに残さずにブリッジから全部消しますか？\n' +
          '全員の画面から消えます。元には戻せません（残したいときは「大会を締めてアーカイブ」）。リンクや時間帯の設定は残ります。',
      )
    ) {
      return;
    }
    // この端末で貼り付けたもの（端末にだけ残る）も一緒に消す
    if (await submitConfig({ clearData: true }, '取り込んだ情報を消しました（この端末で貼り付けたものも消しました）')) feed.clearLocal();
  };
  $<HTMLButtonElement>('pb-cfg-clear').onclick = () => {
    if (!confirm('ブリッジに登録したオクレンジャーのリンクを消しますか？（取り込んだ風は残ります）')) return;
    void submitConfig({ link: null }, 'リンクを消しました');
  };

  // --- 開け閉め・高さ ------------------------------------------------------------
  const applyHeight = () => host.style.setProperty('height', `${Math.round(prefs.sheetRatio * 100)}dvh`);
  const open = async () => {
    applyHeight();
    host.hidden = false;
    renderClp();
    showTab(prefs.tab);
    renderConfig();
    feed.markSeen();
    refreshBadge();
    if (await options.ensureAuth()) await feed.refresh();
  };
  const close = () => {
    host.hidden = true;
  };
  $<HTMLButtonElement>('pb-close').onclick = close;

  // 上端をつまんで高さを変える（タスクシートと同じドラッグ処理。離したことが
  // 届かない・ブラウザに横取りされる場合の扱いも共通、docs/08 §199）
  let startHeight = 0;
  onDrag(head, null, {
    start: () => {
      startHeight = host.getBoundingClientRect().height;
      host.classList.add('dragging');
    },
    move: (_dx, dy) => {
      host.style.height = `${Math.max(0, startHeight - dy)}px`;
    },
    end: (moved, _dx, dy) => {
      host.classList.remove('dragging');
      if (!moved) return;
      const snapped = snapSheetRatio((startHeight - dy) / window.innerHeight);
      if (snapped === null) {
        close();
        return;
      }
      prefs.sheetRatio = snapped;
      savePrefs(prefs);
      applyHeight();
    },
  });

  // --- 新着の印 ---------------------------------------------------------------
  const refreshBadge = () => {
    const button = document.getElementById('official-wind-open');
    if (!button) return;
    if (!host.hidden) feed.markSeen();
    const unseen = feed.hasUnseen();
    // 新しい計測のお知らせで自分のチーム（★）が一覧に無ければ、印を「要確認」の色に
    const alert = unseen && latestOwnTeamOut(feed.markerNotices, options.venueId());
    button.classList.toggle('has-new', unseen && !alert);
    button.classList.toggle('has-alert', alert);
    button.setAttribute(
      'aria-label',
      alert ? '風・計測（要確認：自分のチームが計測の一覧に無い）' : unseen ? '風（大会のパイバル）・新しい配信あり' : '風（大会のパイバル）',
    );
  };

  feed.onChange(() => {
    if (!host.hidden) {
      if (prefs.tab === 'wind') render();
      else markers.render();
      renderConfig();
    }
    refreshBadge();
  });

  return {
    toggle: () => {
      if (host.hidden) void open();
      else close();
    },
    refreshBadge,
    /** 共有メニューからGV4Dに本文が渡されたとき（Androidの共有先）。開いて全員に送る */
    receiveShared: async (text: string) => {
      await open();
      const more = host.querySelector<HTMLDetailsElement>('#pb-paste')?.closest('details');
      if (more) more.open = true;
      paste.value = text;
      await sendToAll(text, 'share');
    },
  };
}
